import { MapPin, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { requestErrorMessage } from '@apps/web/features/coverage/model/request_model';
import { getApiClient } from '@apps/web/lib/api_client';
import { type WorkforceMapPoint } from '@libraries/typescript/api_client';

export type PotentialClientPin = {
    readonly latitude: number;
    readonly longitude: number;
    readonly formattedAddress: string;
};

const NEAREST_CAREGIVER_COUNT = 4;

function milesBetween(
    origin: { readonly latitude: number; readonly longitude: number },
    destination: { readonly latitude: number; readonly longitude: number },
): number {
    const toRadians = Math.PI / 180;
    const deltaLatitude = (destination.latitude - origin.latitude) * toRadians;
    const deltaLongitude =
        (destination.longitude - origin.longitude) * toRadians;
    const haversineTerm =
        Math.sin(deltaLatitude / 2) ** 2 +
        Math.cos(origin.latitude * toRadians) *
            Math.cos(destination.latitude * toRadians) *
            Math.sin(deltaLongitude / 2) ** 2;
    return 3958.8 * 2 * Math.asin(Math.sqrt(haversineTerm));
}

export function PotentialClientSearchCard({
    organizationId,
    points,
    colorByOffice,
    unassignedOfficeColor,
    plotted,
    onPlotted,
    onClear,
    onSelectCaregiver,
}: {
    readonly organizationId: string | null;
    readonly points: ReadonlyArray<WorkforceMapPoint>;
    readonly colorByOffice: ReadonlyMap<string, string>;
    readonly unassignedOfficeColor: string;
    readonly plotted: PotentialClientPin | null;
    readonly onPlotted: (pin: PotentialClientPin) => void;
    readonly onClear: () => void;
    readonly onSelectCaregiver: (point: WorkforceMapPoint) => void;
}) {
    const [query, setQuery] = useState('');
    const [isPlotting, setIsPlotting] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const activeLookupRef = useRef<symbol | null>(null);

    useEffect(() => {
        activeLookupRef.current = null;
        setQuery('');
        setMessage(null);
        setIsPlotting(false);
    }, [organizationId]);

    const nearestCaregivers = useMemo(() => {
        if (!plotted) {
            return [];
        }
        return points
            .map((point) => ({
                point,
                miles: milesBetween(plotted, point),
            }))
            .sort((a, b) => a.miles - b.miles)
            .slice(0, NEAREST_CAREGIVER_COUNT);
    }, [plotted, points]);

    const plotAddress = async () => {
        const trimmedQuery = query.trim();
        if (!organizationId || trimmedQuery.length < 3 || isPlotting) {
            return;
        }
        const requestToken = Symbol('potential-client-geocode');
        activeLookupRef.current = requestToken;
        setIsPlotting(true);
        setMessage(null);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/coverage/map/potential-client-geocode',
                pathParams: { organization_id: organizationId },
                body: { address: trimmedQuery },
                reason: 'coverage-potential-client',
            });
            if (activeLookupRef.current !== requestToken) {
                return;
            }
            if (response.result) {
                onPlotted({
                    latitude: response.result.latitude,
                    longitude: response.result.longitude,
                    formattedAddress: response.result.formatted_address,
                });
            } else {
                setMessage(
                    'We couldn’t find that address. Try adding a city and state.',
                );
            }
        } catch (requestError) {
            if (activeLookupRef.current === requestToken) {
                setMessage(
                    requestErrorMessage(
                        requestError,
                        'We couldn’t look up that address.',
                    ),
                );
            }
        } finally {
            if (activeLookupRef.current === requestToken) {
                activeLookupRef.current = null;
                setIsPlotting(false);
            }
        }
    };

    const clear = () => {
        activeLookupRef.current = null;
        setQuery('');
        setMessage(null);
        setIsPlotting(false);
        onClear();
    };

    return (
        <div className="border-border bg-background w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border shadow-lg">
            <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
                <MapPin
                    className="size-3.5 text-violet-600"
                    aria-hidden="true"
                />
                <span className="text-xs font-semibold">Potential client</span>
            </div>
            <form
                className="flex gap-1.5 px-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    void plotAddress();
                }}
            >
                <Input
                    className="h-8 text-xs"
                    placeholder="Street, city, state, or ZIP"
                    aria-label="Potential client address"
                    autoComplete="off"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                />
                <Button
                    type="submit"
                    size="sm"
                    className="h-8"
                    disabled={
                        isPlotting || !organizationId || query.trim().length < 3
                    }
                >
                    {isPlotting ? 'Plotting…' : 'Plot'}
                </Button>
            </form>
            <p className="text-muted-foreground px-3 pt-1.5 pb-2.5 text-[11px]">
                Sent to our map provider when you click Plot. Phoebe doesn’t
                save it.
            </p>
            {message ? (
                <p
                    className="text-destructive px-3 pb-2.5 text-xs"
                    role="alert"
                >
                    {message}
                </p>
            ) : null}
            {plotted ? (
                <div className="border-border border-t">
                    <div className="flex items-center gap-2 bg-violet-50 px-3 py-2">
                        <MapPin
                            className="size-3.5 shrink-0 fill-violet-600 text-violet-600"
                            aria-hidden="true"
                        />
                        <span className="grow truncate text-xs font-medium">
                            {plotted.formattedAddress}
                        </span>
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground shrink-0"
                            aria-label="Remove potential client address from map"
                            onClick={clear}
                        >
                            <X className="size-3.5" />
                        </button>
                    </div>
                    {nearestCaregivers.length > 0 ? (
                        <div className="pb-2">
                            <p className="text-muted-foreground px-3 pt-2 pb-1 text-[11px] font-medium">
                                Nearest mapped caregivers
                            </p>
                            {nearestCaregivers.map(({ point, miles }) => (
                                <button
                                    key={point.caregiver_id}
                                    type="button"
                                    className="hover:bg-muted/60 flex w-full items-center gap-2 px-3 py-1.5 text-left"
                                    onClick={() => onSelectCaregiver(point)}
                                >
                                    <span
                                        className="size-2 shrink-0 rounded-full"
                                        style={{
                                            backgroundColor: point.office_name
                                                ? (colorByOffice.get(
                                                      point.office_name,
                                                  ) ?? unassignedOfficeColor)
                                                : unassignedOfficeColor,
                                        }}
                                        aria-hidden="true"
                                    />
                                    <span className="grow truncate text-xs">
                                        {point.name}
                                    </span>
                                    <span className="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                                        {miles.toFixed(1)} mi
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted-foreground px-3 py-2 text-xs">
                            No mapped caregivers to compare yet.
                        </p>
                    )}
                </div>
            ) : null}
        </div>
    );
}
